/* Importar un Excel a FiboSheets: leer el archivo, dejarle lugar para seguir
 * cargando, armar el aviso de qué se trajo y qué no, y —en el editor— sumar
 * sus hojas a la planilla abierta.
 *
 * ── POR QUÉ APARTE DE LA PANTALLA ──────────────────────────────────────────
 *
 * Se importa desde dos lugares (la lista de «Mis planillas», que crea una
 * planilla nueva, y el editor, que suma hojas) y de dos maneras (el botón y
 * arrastrar el archivo). Todo eso termina acá; `routes/MiEspacio` sólo decide
 * qué hacer con las hojas que vuelven. Lo de traer cada celda, fórmula y
 * estilo está en `shared/planillaExcel` y `shared/planillaEstilosExcel`.
 *
 * ── EL AVISO ───────────────────────────────────────────────────────────────
 *
 * La regla de siempre: nunca mostrar un número distinto al de Excel sin
 * avisar. El aviso dice cuántas fórmulas quedaron vivas y cuántas como valor
 * fijo (y cuántas se calculan acá porque el archivo no traía su resultado, y
 * cuántas quedaron fijas por usar otra fija), y ahora también lo que no se pudo traer tal cual: colores llevados a
 * la paleta del hub, bordes, tamaños de letra, formato condicional, listas
 * desplegables, combinadas que se pisaban. Y, antes que nada, las hojas que
 * vinieron sin sus fórmulas porque SheetJS no las pudo leer (ver `leerLibro`).
 */

import { importarLibro } from "./planillaExcel";
import { matrizVacia, renombrarHojasEnFormula } from "./planilla";
import { NO_SOPORTADO } from "./planillaEstilosExcel";
import { TOPE_IMPORTAR_MB, TOPE_PLANILLA_MB } from "./topes";

/* Lo que se acepta: el `accept` del selector de archivos y la prueba al soltar.
   El .xlsb (Excel binario) también: es como guarda Satelital su planilla de
   satelitales y choferes, y SheetJS lo lee (sin los colores: ver el aviso). */
export const ACEPTA = ".xlsx,.xlsm,.xlsb,.xls,.csv";
export const esArchivoExcel = (nombre) => /\.(xlsx|xlsm|xlsb|xls|csv)$/i.test(String(nombre || ""));
const extension = (nombre) => (/\.([^.]+)$/.exec(String(nombre || "")) || [])[1]?.toLowerCase() || "";

/* El nombre de la planilla que se crea: el del archivo sin la extensión. */
export const nombreDeArchivo = (nombre) => String(nombre || "").replace(/\.[^.]+$/, "") || "Importada";

/* `cellStyles` trae los anchos de columna y los fondos; `bookFiles` deja a
   mano el XML de cada hoja, de donde salen la negrita, los colores de letra
   y la alineación (ver `shared/planillaEstilosExcel`). */
export const OPCIONES_LECTURA = { type: "array", cellFormula: true, cellNF: true, cellStyles: true, bookFiles: true };

const COLS_MIN = 26; // hasta la Z, como una planilla nueva

/* Lugar para seguir cargando: columnas hasta la Z (y una de más) y unas
   filas de más. */
export function conLugar(hojas) {
  return hojas.map((h) => {
    const cols = Math.max(COLS_MIN, (h.celdas[0] ? h.celdas[0].length : 0) + 1);
    const filas = Math.max(50, h.celdas.length + 20);
    const celdas = Array.from({ length: filas }, (_, r) => {
      const g = h.celdas[r] ? h.celdas[r].slice() : [];
      while (g.length < cols) g.push("");
      return g;
    });
    return { ...h, celdas };
  });
}

/* Los bytes de un File. `arrayBuffer()` no está en todos lados (en navegadores
   viejos ni en jsdom): ahí, el FileReader de siempre. */
export function bytesDeArchivo(archivo) {
  if (archivo && typeof archivo.arrayBuffer === "function") return archivo.arrayBuffer();
  return new Promise((ok, mal) => {
    const lector = new FileReader();
    lector.onload = () => ok(lector.result);
    lector.onerror = () => mal(lector.error);
    lector.readAsArrayBuffer(archivo);
  });
}

/* SheetJS lee cada hoja por separado y, si algo de una hoja le falla, la deja
   afuera del libro SIN avisar (no tira). Pasa con una fórmula que no sabe
   leer: en un .xlsb, una referencia a una tabla que trae datos de más (como
   la de una tabla de otro libro) o un «rótulo en fórmula» de Excel viejo. Por
   una sola fórmula así, la hoja entera llegaba vacía. Entonces, si falta
   alguna, se lee el archivo otra vez sin fórmulas y esas hojas vienen con el
   valor que mostraba Excel en cada celda (sus fórmulas, como números fijos).
   Devuelve el libro y los nombres de las hojas que vinieron así
   (`soloValores`) y de las que ni así se pudieron leer (`sinLeer`), para el
   aviso. */
export function leerLibro(XLSX, bytes) {
  const wb = XLSX.read(bytes, OPCIONES_LECTURA);
  const faltan = wb.SheetNames.filter((n) => !wb.Sheets[n]);
  const soloValores = [];
  const sinLeer = [];
  if (faltan.length) {
    let otra = null;
    try { otra = XLSX.read(bytes, { ...OPCIONES_LECTURA, cellFormula: false }); } catch { otra = null; }
    for (const n of faltan) {
      const ws = otra && otra.Sheets && otra.Sheets[n];
      if (ws) { wb.Sheets[n] = ws; soloValores.push(n); } else sinLeer.push(n);
    }
  }
  return { wb, soloValores, sinLeer };
}

/* Un archivo → `{ hojas, aviso, resumen }`. Las hojas ya traen lugar para
   seguir cargando. SheetJS se carga recién acá (no se suma al arranque del
   hub). Si el archivo no se puede leer, tira. */
export async function leerExcel(archivo, XLSXpropio = null) {
  const XLSX = XLSXpropio || (await import("xlsx"));
  const { wb, soloValores, sinLeer } = leerLibro(XLSX, await bytesDeArchivo(archivo));
  const { hojas, ...cuentas } = importarLibro(XLSX, wb);
  const resumen = { ...cuentas, soloValores, sinLeer, tipo: extension(archivo && archivo.name) };
  return { hojas: conLugar(hojas), aviso: avisoDeImportacion(resumen), resumen };
}

/* ── El aviso ───────────────────────────────────────────────────────────── */

const plural = (n, una, varias) => (n === 1 ? `1 ${una}` : `${n} ${varias}`);

/* Las fórmulas: cuántas quedaron vivas y cuántas fijas, y —si pasó— cuántas
   se calculan acá porque el archivo no traía su resultado, cuántas quedaron
   fijas por usar otra fija y cuántas dan distinto (ver `importarLibro`). Lo
   usa también el aviso del bot (`shared/importacionesBot`). */
export function avisoFormulas({ vivas = 0, congeladas = 0, arrastradas = 0, recalculadas = 0, revisar = 0 } = {}) {
  const trajo = vivas === 1 ? "Se trajo 1 fórmula" : `Se trajeron ${vivas} fórmulas`;
  if (vivas && !congeladas && !recalculadas && !revisar) {
    return `${trajo}, y ${vivas === 1 ? "da" : "todas dan"} lo mismo que en el Excel.`;
  }
  const frases = [];
  if (vivas) frases.push(`${trajo}.`);
  if (recalculadas) {
    const una = recalculadas === 1;
    const quienes = vivas === recalculadas ? (una ? "Venía" : "Venían") : `De ésas, ${recalculadas} ${una ? "venía" : "venían"}`;
    frases.push(`${quienes} sin el resultado de Excel (el archivo traía un 0 en su lugar, como pasa con los .xls guardados con otros programas), así que ${una ? "se calcula" : "se calculan"} acá: ${una ? "revisá que dé" : "revisá que den"} lo que esperás.`);
  }
  if (congeladas) {
    const arr = arrastradas ? ` (${arrastradas === 1 ? "1 de ellas, porque usa otra que quedó fija" : `${arrastradas} de ellas, porque usan otras que quedaron fijas`})` : "";
    const fijas = congeladas === 1
      ? "1 fórmula quedó como número fijo, con el valor que tenía en el Excel, porque acá no se calcula igual: revisala si la vas a seguir usando."
      : `${congeladas} fórmulas quedaron como números fijos, con el valor que tenían en el Excel, porque acá no se calculan igual${arr}: revisalas si las vas a seguir usando.`;
    frases.push(vivas ? `Aparte, ${fijas}` : `Ojo: ${fijas}`);
  }
  if (revisar) {
    frases.push(`Ojo: ${plural(revisar, "fórmula da", "fórmulas dan")} acá distinto que en el Excel: ${revisar === 1 ? "revisala" : "revisalas"}.`);
  }
  return frases.length ? frases.join(" ") : null;
}

/* El formato: lo que no se pudo traer tal cual. */
function avisoFormato(estilos, tipo) {
  if (!estilos) return [];
  const partes = [];
  if (tipo === "xls") {
    partes.push("Del Excel viejo (.xls) sólo se pueden leer los fondos de las celdas: la negrita, los colores de letra y la alineación no vienen. Si lo guardás como .xlsx y lo importás de nuevo, se traen.");
  }
  // Del .xlsb no viene nada del formato de las celdas (ni los fondos): se
  // avisa sólo si el libro tenía algo más que formatos de número (ver
  // `hayFormatoSinLeer`). Los formatos de número, los anchos y las
  // combinadas sí vienen.
  if (tipo === "xlsb" && estilos.formatoSinLeer) {
    partes.push("Del Excel binario (.xlsb) no se puede leer el formato de las celdas: los fondos, la negrita, los colores de letra y la alineación no vienen. Los valores están todos. Si lo guardás como .xlsx y lo importás de nuevo, se traen.");
  }
  if (estilos.aproximados) {
    partes.push(`${plural(estilos.aproximados, "celda cambió", "celdas cambiaron")} de color para usar el más parecido de la paleta del hub.`);
  }
  // Siempre en el mismo orden (el de NO_SOPORTADO), no en el que aparecieron.
  const cuantas = estilos.faltan || {};
  const faltan = Object.keys(NO_SOPORTADO)
    .filter((k) => cuantas[k] > 0)
    .map((k) => `${NO_SOPORTADO[k]} (${plural(cuantas[k], "celda", "celdas")})`);
  if (estilos.condicional) faltan.push(`formato condicional (${plural(estilos.condicional, "regla", "reglas")})`);
  if (estilos.validacion) faltan.push(`listas desplegables (${plural(estilos.validacion, "lista", "listas")})`);
  if (faltan.length) {
    const lista = faltan.length > 1 ? `${faltan.slice(0, -1).join(", ")} y ${faltan[faltan.length - 1]}` : faltan[0];
    partes.push(`No se pudo traer: ${lista}. Los valores están todos.`);
  }
  if (estilos.separadas) {
    partes.push(`${plural(estilos.separadas, "celda combinada se separó", "celdas combinadas se separaron")} porque se pisaba${estilos.separadas === 1 ? "" : "n"} con otra: el valor quedó en la celda de arriba a la izquierda.`);
  }
  return partes;
}

/* Cuando el Excel entero no entra en una planilla (la base tiene un tope de
   TOPE_PLANILLA_MB por planilla). El tamaño es una estimación: el JSON de lo que se iba a
   guardar, con lo que agranda la base al guardarlo (~1,25 veces). Si la cuenta
   da justo cerca del tope, no se arriesga un número. */
export const LEER_MAL = "No pude leer ese archivo. ¿Es un Excel (.xlsx, .xlsb o .xls) o un CSV?";
export function textoMuyGrande(datos) {
  let mb = 0;
  try { mb = (JSON.stringify(datos) || "").length * 1.25 / 1e6; } catch { mb = 0; }
  const cuanto = mb >= TOPE_PLANILLA_MB * 1.1
    ? `(unos ${mb.toFixed(1).replace(".", ",")} MB, y el tope es ${TOPE_PLANILLA_MB} MB)`
    : `(pasa el tope de ${TOPE_PLANILLA_MB} MB)`;
  return `Ese Excel es demasiado grande para una planilla ${cuanto}. Borrale en el Excel las hojas que no necesitás o las filas viejas, y probá de nuevo.`;
}

/* Cuando el ARCHIVO pasa el tope de importación: no se llega a abrir. */
export function textoArchivoGrande(bytes) {
  const mb = (Number(bytes) || 0) / (1024 * 1024);
  const pesa = mb ? `pesa ${mb.toFixed(1).replace(".", ",")} MB y ` : "";
  return `Ese archivo ${pesa}el tope para importar es de ${TOPE_IMPORTAR_MB} MB. Borrale en el Excel las hojas que no necesitás o las filas viejas, y probá de nuevo.`;
}

/* Lo que se le dice a la persona después de importar, o null si no hay nada
   que contar. */
export function avisoDeImportacion({ estilos = null, tipo = "", soloValores = [], sinLeer = [], ...cuentas } = {}) {
  const partes = [avisoHojas(soloValores, sinLeer), avisoFormulas(cuentas), ...avisoFormato(estilos, tipo)].filter(Boolean);
  return partes.length ? partes.join(" ") : null;
}

/* Las hojas que SheetJS no pudo leer con sus fórmulas (ver `leerLibro`). Va
   primero: es lo que más cambia lo que se ve. */
const listaDeHojas = (nombres) => {
  const q = nombres.map((n) => `«${n}»`);
  return q.length > 1 ? `${q.slice(0, -1).join(", ")} y ${q[q.length - 1]}` : q[0];
};
function avisoHojas(soloValores = [], sinLeer = []) {
  const partes = [];
  if (soloValores.length) {
    const una = soloValores.length === 1;
    partes.push(una
      ? `La hoja ${listaDeHojas(soloValores)} tiene fórmulas que no se pueden leer, así que vino con los valores que mostraba el Excel: todas sus fórmulas quedaron como números fijos.`
      : `Las hojas ${listaDeHojas(soloValores)} tienen fórmulas que no se pueden leer, así que vinieron con los valores que mostraba el Excel: todas sus fórmulas quedaron como números fijos.`);
  }
  if (sinLeer.length) {
    partes.push(sinLeer.length === 1
      ? `La hoja ${listaDeHojas(sinLeer)} no se pudo leer: quedó vacía.`
      : `Las hojas ${listaDeHojas(sinLeer)} no se pudieron leer: quedaron vacías.`);
  }
  return partes.length ? partes.join(" ") : null;
}

/* ── Sumar las hojas de un Excel a la planilla abierta ──────────────────── */

/* Un nombre que no choque con los que ya hay: «Viajes (2)», «Viajes (3)»… */
function nombreLibre(nombre, usados) {
  const base = String(nombre || "Hoja").trim() || "Hoja";
  if (!usados.has(base.toLowerCase())) return base;
  for (let i = 2; ; i += 1) {
    const n = `${base} (${i})`;
    if (!usados.has(n.toLowerCase())) return n;
  }
}

/* Las hojas de la planilla + las del Excel, al final. Si un nombre choca, la
   hoja nueva se renombra, y las fórmulas de las hojas nuevas que la nombran
   (=SUMA(Viajes!C3:C4)) pasan a nombrar a la renombrada: siguen mirando a la
   hoja que vino en su mismo archivo, no a la que ya estaba. Las hojas que ya
   estaban no se tocan. Devuelve `{ hojas, agregadas, renombradas: [{ de, a }] }`
   (`agregadas`: las del Excel, ya con su nombre final). */
export function sumarHojas(existentes, nuevas) {
  const usados = new Set(existentes.map((h) => String(h.nombre).toLowerCase()));
  const mapa = new Map(); // nombre en el archivo (minúsculas) → nombre nuevo
  const renombradas = [];
  const nombres = nuevas.map((h) => {
    const n = nombreLibre(h.nombre, usados);
    usados.add(n.toLowerCase());
    if (n !== h.nombre) { mapa.set(String(h.nombre).toLowerCase(), n); renombradas.push({ de: h.nombre, a: n }); }
    return n;
  });
  const agregadas = nuevas.map((h, i) => {
    const celdas = !mapa.size ? h.celdas : h.celdas.map((fila) => {
      let nueva = null;
      fila.forEach((v, c) => {
        if (typeof v !== "string" || !v.startsWith("=")) return;
        const a = renombrarHojasEnFormula(v, mapa);
        if (a !== v) { if (!nueva) nueva = fila.slice(); nueva[c] = a; }
      });
      return nueva || fila;
    });
    return { ...h, nombre: nombres[i], celdas: celdas.length ? celdas : matrizVacia(1, 1) };
  });
  return { hojas: [...existentes, ...agregadas], agregadas, renombradas };
}

/* El aviso de «Importar Excel como hojas nuevas»: qué hojas se sumaron (las
   `agregadas` de `sumarHojas`), cuáles se renombraron, y después lo de
   siempre (fórmulas y formato). */
export function avisoDeHojasNuevas(nuevas, renombradas, avisoBase) {
  const n = nuevas.length;
  const partes = [n === 1 ? `Se sumó la hoja «${nuevas[0].nombre}».` : `Se sumaron ${n} hojas: ${nuevas.map((h) => `«${h.nombre}»`).join(", ")}.`];
  if (renombradas.length) {
    const cuales = renombradas.map((x) => `«${x.de}» quedó como «${x.a}»`).join(", ");
    partes.push(`Como ya había hojas con esos nombres, ${cuales}; las fórmulas del Excel que las usaban ya apuntan al nombre nuevo.`);
  }
  if (avisoBase) partes.push(avisoBase);
  return partes.join(" ");
}
